// @ts-nocheck
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import scrollToId from "@store/Framework/Common/Utilities/scrollToId";
import { windowLocation } from "@store/Framework/Common/Utilities/WindowLocation";
import AnchorHeading, { AnchorHeadingStyles } from "@store/Library/Components/Toolkit/Anchor/AnchorHeading";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { FC, useEffect, useState } from "react";

const enum fields {
    anchorType = "anchorType",
    title = "title",
    anchor = "anchor",
    anchorLabel = "anchorLabel",
    direction = "direction",
    offset = "offset",
}

interface Props extends WidgetProps, ExpandStyles<ProductDetailsAnchorHeadingStyles> {
    fields: {
        [fields.anchorType]: string;
        [fields.title]: string;
        [fields.anchor]: string;
        [fields.anchorLabel]: string;
        [fields.direction]: string;
        [fields.offset]: string;
    };
}

function stripChar(string: string, char: string) {
    return string.substring(string.indexOf(char) + 1);
}

export interface ProductDetailsAnchorHeadingStyles {
    anchorHeading?: AnchorHeadingStyles;
}

export const productDetailsAnchorHeadingStyles: ProductDetailsAnchorHeadingStyles = {
    anchorHeading: {
        title: {
            variant: "h2",
            forwardAs: "h2",
            color: "primary",
        },
    },
};

const ProductDetailsAnchorHeading: FC<Props> = ({
    fields: { anchorType, title, anchor, anchorLabel, direction, offset },
    extendedStyles,
    overridedStyles,
}) => {
    const styles = useExpandProps(productDetailsAnchorHeadingStyles, extendedStyles, overridedStyles);
    const initLocation = windowLocation(); // hack because location hash not picked up on pageload

    useEffect(() => {
        let anchor = "";
        if (initLocation && initLocation.hash) {
            anchor = stripChar(initLocation.hash, "#");
        }
        if (anchor) {
            scrollToId(anchor);
        }
    }, []);

    return (
        <AnchorHeading
            type={anchorType}
            direction={direction}
            offset={offset}
            title={title}
            anchor={anchor}
            label={anchorLabel}
            extendedStyles={styles.anchorHeading}
        />
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsAnchorHeading,
    definition: {
        group: "Basic",
        displayName: "Anchor",
        icon: "anchor",
        fieldDefinitions: [
            {
                name: fields.anchorType,
                displayName: "Anchor Type",
                editorTemplate: "RadioButtonsField",
                defaultValue: "heading",
                fieldType: "General",
                sortOrder: 0,
                options: [
                    {
                        displayName: "Heading (title element)",
                        value: "heading",
                    },
                    {
                        displayName: "Invisible",
                        value: "invisible",
                    },
                ],
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isVisible: x => x.fields.anchorType === "heading",
            },
            {
                name: fields.anchor,
                displayName: "Anchor",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
                defaultValue: "",
            },
            {
                name: fields.anchorLabel,
                displayName: "Anchor menu label override",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.direction,
                displayName: "Offset Direction",
                editorTemplate: "RadioButtonsField",
                defaultValue: "top",
                fieldType: "General",
                sortOrder: 2,
                isVisible: x => x.fields.anchorType === "invisible",
                options: [
                    {
                        displayName: "Offset to Top",
                        value: "top",
                    },
                    {
                        displayName: "Offset to Bottom",
                        value: "bottom",
                    },
                ],
            },
            {
                name: fields.offset,
                displayName: "Offset Distance in Pixels",
                editorTemplate: "TextField",
                defaultValue: "0",
                fieldType: "General",
                sortOrder: 3,
                isVisible: x => x.fields.anchorType === "invisible",
            },
        ],
    },
};

export default widgetModule;
